import {
  CallStateLockInitiator,
  CallStateStudentLockReason,
  CallStateSystemLockReason,
} from '@engoo/realtime-protocol/callState';
import { BookingCancellationType } from 'app/constants/enums/BookingCancellationType';
import { CancellationReason } from 'app/constants/enums/CancellationReason';
import { BookingState, BookingType } from 'app/models/rtc/booking/constants';
import { LessonBookingOutcomeType } from 'app/models/rtc/booking/lessons/outcome';
import { LockState } from 'app/models/rtc/callState/LockState';

export enum CallOutcomeType {
  /**
   * Outcome if there is a cancellation.
   */
  Cancelled,
  /**
   * Outcome from the a user ending the lesson.
   */
  Complete,
  /**
   * When the system automatically ends the lesson.
   */
  TimedOut,
}

interface ICancelledCallOutcome {
  cancellationReason: CancellationReason | null;
  cancellationType: BookingCancellationType;
  time?: Date | null;
  type: CallOutcomeType.Cancelled;
}

interface ICompleteLockCallOutcome {
  time?: Date | null;
  type: CallOutcomeType.Complete;
}

interface ITimedOutCallOutcome {
  time: Date | null;
  type: CallOutcomeType.TimedOut;
}

/**
 * Describes possible outcomes for a call that the UI is concerned about.
 */
export type CallOutcome = ICancelledCallOutcome | ICompleteLockCallOutcome | ITimedOutCallOutcome;

/**
 * A best effort attempt to derive a call outcome from the booking state and lock state. the lock state and
 * booking state into a single call outcome.
 *
 * @returns null if no outcome can be derived, otherwise produces a CallOutcome.
 */
export const createCallOutcome = (bookingState: BookingState, lockState: LockState | null): CallOutcome | null => {
  // Check cancellation first, since it can be changed post lesson. This may only be reflected completely by
  // the backend model so it should first and foremost be taken as the truth. The realtime server also guarantees
  // that the backend model will be update when presenting a cancellation type.
  if (
    bookingState.bookingType === BookingType.Lesson &&
    bookingState.outcome?.type === LessonBookingOutcomeType.Cancelled
  ) {
    return {
      cancellationReason: bookingState.outcome.cancellationReason,
      cancellationType: bookingState.outcome.cancellationType,
      time: bookingState.outcome.time,
      type: CallOutcomeType.Cancelled,
    };
  }

  // If the system locks the lesson it is presumed to be an automatic time out.
  // This case can only be derived from the lockState and not the booking state.
  // It's important that this check is performed first as it may overlap with
  // other end states.
  if (
    lockState !== null &&
    lockState.lockInitiator === CallStateLockInitiator.System &&
    lockState.lockReason === CallStateSystemLockReason.EndLesson
  ) {
    return {
      time: lockState.lockTime,
      type: CallOutcomeType.TimedOut,
    };
  }

  // If the student actively ended the lesson and mark as a complete outcome.
  // The end time is more reliable from the real time server in the event of
  // a user end lesson event, so check the lock state first.
  if (
    lockState !== null &&
    lockState.lockInitiator === CallStateLockInitiator.Student &&
    lockState.lockReason === CallStateStudentLockReason.EndLesson
  ) {
    return {
      time: lockState.lockTime,
      type: CallOutcomeType.Complete,
    };
  }

  // Fallback to checking the booking state for completeness.
  if (
    bookingState.bookingType === BookingType.Lesson &&
    bookingState.outcome?.type === LessonBookingOutcomeType.Ended
  ) {
    return {
      time: bookingState.outcome.time,
      type: CallOutcomeType.Complete,
    };
  }

  return null;
};
